export type FormulaTokenKind = "variable" | "operator" | "grouping";

export interface FormulaExpressionToken {
  kind: FormulaTokenKind;
  value: string;
}

export type FormulaExpressionInput = string | readonly string[];

const operatorToken = /^[×÷*/+=−-]$/;
const groupingToken = /^[(){}]$/;

function tokenFromValue(value: string): FormulaExpressionToken | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (operatorToken.test(trimmed)) {
    return { kind: "operator", value: trimmed };
  }
  if (groupingToken.test(trimmed)) {
    return { kind: "grouping", value: trimmed };
  }
  return {
    kind: "variable",
    value:
      trimmed.startsWith("[") && trimmed.endsWith("]")
        ? trimmed.slice(1, -1).trim()
        : trimmed,
  };
}

function tokenizeString(expression: string): FormulaExpressionToken[] {
  const tokens: FormulaExpressionToken[] = [];
  const pattern = /(\[[^\]]+\]|[×÷*/+=−(){}]|\s[-−]\s)/g;
  let cursor = 0;
  let match = pattern.exec(expression);

  while (match) {
    const before = tokenFromValue(expression.slice(cursor, match.index));
    if (before) tokens.push(before);

    const matched = match[0];
    const normalized = matched.trim();
    const token = tokenFromValue(normalized);
    if (token) tokens.push(token);

    cursor = match.index + matched.length;
    match = pattern.exec(expression);
  }

  const remainder = tokenFromValue(expression.slice(cursor));
  if (remainder) tokens.push(remainder);
  return tokens;
}

/** Converts supported formula strings and legacy token arrays into display tokens. */
export function tokenizeFormula(
  expression: FormulaExpressionInput,
): FormulaExpressionToken[] {
  if (typeof expression === "string") {
    return tokenizeString(expression);
  }
  return expression.flatMap((value) => tokenizeString(value));
}

export interface FormulaExpressionProps {
  expression: FormulaExpressionInput;
  className?: string;
}

/** Displays a human-readable formula with variables visually distinct from math. */
export function FormulaExpression({
  expression,
  className,
}: FormulaExpressionProps) {
  const tokens = tokenizeFormula(expression);
  const fullExpression = tokens.map((token) => token.value).join(" ");

  return (
    <span
      className={className}
      role="img"
      aria-label={`Formula: ${fullExpression}`}
    >
      {tokens.map((token, index) =>
        token.kind === "variable" ? (
          <span
            key={`${token.value}-${index}`}
            className="mr-1 inline-block rounded bg-(--bg-canvas) px-1 py-0.5 font-medium text-(--fg-primary)"
          >
            {token.value}
          </span>
        ) : (
          <span
            key={`${token.value}-${index}`}
            className="mx-0.5 text-(--fg-muted)"
            aria-hidden="true"
          >
            {token.value}
          </span>
        ),
      )}
    </span>
  );
}
